<script setup lang="ts">
import type { BreadcrumbItem } from '~/components/ui/breadcrumb';

import { defaultTwitterCardMeta } from '@/constants/seo';
import * as typeform from '@typeform/embed';
import { useAbsoluteUrl } from '~/composables/useAbsoluteUrl';
import { useMixpanel } from '~/composables/useMixpanel';
import { MixpanelEvents, MixpanelProps } from '~/core/service/mixpanel';
import advertiseProperty from '~~/data/pages/advertiseProperty';

definePageMeta({
	layout: 'default-transparent',
	path: '/anunciar-imovel'
});

const route = useRoute();

const mixpanel = useMixpanel();

const strings = advertiseProperty.pt;

const { user } = useUserSession();

const breadcrumbItems = [
	{ label: strings.breadcrumb.home, route: '/' },
	{
		label: strings.breadcrumb.advertiseProperty,
		route: '/anunciar-imovel'
	}
];

const questions = computed(() => strings.faq.questions);
const contentSection = computed(() => strings.content);

const typeformPopup = ref<ReturnType<typeof typeform.createPopup>>();
const { toAbsoluteUrl } = useAbsoluteUrl();
const ogImageUrl = toAbsoluteUrl(
	'/images/social/openGraph/og_anunciar_meu_imovel.jpg'
);
useSeoMeta({
	title: 'Anunciar Imóvel | PilarHomes',
	description:
		'Destaque seu imóvel no maior portfólio de alto padrão de São Paulo. Aumente a visibilidade da sua propriedade e atraia compradores à altura do seu imóvel!',
	keywords: 'imóvel, alto padrão, São Paulo',
	ogTitle: 'Anunciar Imóvel | PilarHomes',
	ogDescription:
		'Destaque seu imóvel no maior portfólio de alto padrão de São Paulo. Aumente a visibilidade da sua propriedade e atraia compradores à altura do seu imóvel!',
	ogImage: ogImageUrl,
	twitterCard: defaultTwitterCardMeta,
	twitterTitle: 'Anunciar Imóvel | PilarHomes',
	twitterDescription:
		'Destaque seu imóvel no maior portfólio de alto padrão de São Paulo. Aumente a visibilidade da sua propriedade e atraia compradores à altura do seu imóvel!',
	twitterImage: ogImageUrl
});

const { advertisePropertyTypeformId } = useRuntimeConfig().public;

const howItWorksClasses = computed(() => {
	return (index: number) => {
		const length = strings.content.howItWorks.list.length;
		return {
			'grid size-8 min-w-8 place-items-center font-medium text-primary': true,
			'bg-beige-light-2': index + 1 < length,
			'bg-success-light-2': index + 1 === length
		};
	};
});

onMounted(() => {
	typeformPopup.value = typeform.createPopup(advertisePropertyTypeformId);

	mixpanel.track(MixpanelEvents.PAGE_VIEWED, {
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.PREVIOUS_PAGE_URL]: document.referrer,
		[MixpanelProps.EVENT_NAME]: 'page_viewed'
	});
});

const handleOpenTypeform = (section: string) => {
	typeformPopup.value?.open();
	mixpanel.track(MixpanelEvents.FORM_SELLING_CLICKED, {
		[MixpanelProps.CTA_TEXT]: strings.CTA.advertiseMyProperty.label,
		[MixpanelProps.USER_NAME]: user.value?.name || '',
		[MixpanelProps.USER_EMAIL]: user.value?.email || '',
		[MixpanelProps.USER_PHONE]: user.value?.phoneNumber || '',
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: section
	});
};

const handleFaqClick = (question: string, position: number) => {
	if (question) {
		mixpanel.track(MixpanelEvents.FAQ_CLICKED, {
			[MixpanelProps.QUESTION_TEXT]: question,
			[MixpanelProps.CURRENT_PAGE]: route.fullPath,
			[MixpanelProps.QUESTION_POSITION]: position
		});
	}
};

function handleBreadcrumbClicked(item: BreadcrumbItem) {
	mixpanel.track(MixpanelEvents.BREADCRUMB_CLICKED, {
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.CTA_TEXT]: item.label,
		[MixpanelProps.DESTINATION_URL]: item.route,
		[MixpanelProps.CLICK_HOME]: item.route === '/'
	});
}
</script>

<template>
	<div class="relative h-[90dvh] w-full">
		<NuxtImg
			:src="'/images/advertiseProperty/hero.jpeg'"
			class="absolute inset-0 size-full object-cover object-[0_45%]"
			sizes="500px md:100vw"
			format="webp"
			loading="lazy"
			preload
			fetchpriority="high"
		/>
		<div class="absolute inset-0 bg-primary/38"></div>
		<Container
			class="absolute z-20 flex size-full"
			main-class="flex flex-col gap-5 md:gap-0 md:flex-row justify-end md:items-end md:justify-between"
		>
			<div class="flex flex-col gap-5 md:w-3/5">
				<h1
					class="whitespace-nowrap font-matter-sq text-[32px] font-normal leading-[110%] tracking-[-0.96px] text-white md:text-5xl"
				>
					{{ strings.hero.title }}
				</h1>
				<h2 class="text-base font-normal leading-normal text-white md:text-xl">
					{{ strings.hero.description }}
				</h2>
			</div>
			<Button
				class="size-min px-6 py-3 md:text-lg"
				@click="handleOpenTypeform('hero section')"
			>
				{{ strings.CTA.advertiseMyProperty.label }}

				<Component :is="strings.CTA.advertiseMyProperty.icon" class="size-6" />
			</Button>
		</Container>
	</div>

	<Container class="block bg-white pb-0! pt-6 md:pt-6">
		<Breadcrumb
			:items="breadcrumbItems"
			@breadcrumb-clicked="handleBreadcrumbClicked"
		/>
	</Container>

	<Container
		class="bg-white"
		main-class="grid grid-cols-1 md:grid-cols-3 gap-8 "
	>
		<div
			v-for="benefit in contentSection.benefits"
			:key="`benefit: ${benefit.title}`"
			class="flex gap-6 md:flex-col md:gap-1"
		>
			<div
				class="before:trapeze relative z-10 h-12 pb-2 before:top-[12px] before:z-0 before:h-6 before:w-[60px] before:bg-beige-light-2 md:w-full"
			>
				<Component
					:is="benefit.icon"
					class="sticky z-10 size-12 text-primary"
				/>
			</div>
			<div>
				<p
					class="font-matter-sq text-xl font-normal leading-normal tracking-[-0.4px] text-primary"
				>
					{{ benefit.title }}
				</p>

				<p class="text-sm font-normal leading-normal text-primary-light-3">
					{{ benefit.description }}
				</p>
			</div>
		</div>
	</Container>

	<Separator class="bg-white-dark-2" />

	<Container main-class="flex gap-8 flex-col bg-white" class="bg-white">
		<h4
			class="font-matter-sq text-[28px] font-normal leading-normal tracking-[-0.56px] text-primary md:text-4xl md:tracking-[-0.72px]"
		>
			{{ strings.content.howItWorks.title }}
		</h4>

		<div class="grid grid-cols-1 gap-11 md:grid-cols-2">
			<div class="relative h-[320px] min-h-[320px]">
				<NuxtImg
					:src="'/images/advertiseProperty/how-it-works.jpeg'"
					format="webp"
					class="size-full object-cover object-[100%_100%]"
				/>
			</div>

			<div class="flex flex-col justify-center gap-8">
				<div
					v-for="(item, index) in strings.content.howItWorks.list"
					:key="`how-it-works: ${item}`"
					class="flex items-center gap-4"
				>
					<span :class="howItWorksClasses(index)">
						<template v-if="index + 1 < strings.content.howItWorks.list.length">
							{{ index + 1 }}
						</template>
						<IconMaterialSymbolsLightCheck
							v-if="index + 1 === strings.content.howItWorks.list.length"
							class="size-6"
						/>
					</span>
					<p
						class="font-matter-sq text-lg font-normal leading-normal tracking-[-0.4px] text-primary md:text-xl"
					>
						{{ item }}
					</p>
				</div>
			</div>
		</div>

		<Card
			class="flex flex-col items-center justify-center gap-4 bg-secondary p-4 md:p-8"
		>
			<CardHeader class="p-0">
				<CardTitle class="text-xl text-primary md:text-2xl">
					{{ strings.advertise.title }}
				</CardTitle>
				<CardDescription class="hidden"></CardDescription>
			</CardHeader>
			<CardContent
				class="p-0 text-center text-sm font-normal leading-loose text-primary-light-3 md:text-base"
			>
				<p>
					{{ strings.advertise.description }}
				</p>
			</CardContent>
			<CardFooter class="mt-auto h-auto p-0">
				<Button
					class="size-min px-6 py-3 md:text-lg"
					@click="handleOpenTypeform('page content')"
				>
					{{ strings.CTA.advertiseMyProperty.label }}

					<Component
						:is="strings.CTA.advertiseMyProperty.icon"
						class="size-6"
					/>
				</Button>
			</CardFooter>
		</Card>
	</Container>

	<Container class="bg-white-dark-1">
		<ContentBlock
			variant="white-dark-1"
			as="div"
			:title="strings.faq.title"
			content-class="space-y-8 p-0 max-w-[940px] mx-auto bg-transparent md:p-0"
			title-class="max-w-[940px] mx-auto"
			class="p-0"
		>
			<QuestionsAccordion
				:questions="questions"
				class="bg-white"
				@question-clicked="e => handleFaqClick(e.question, e.position)"
			/>

			<div class="tablet-desktop-only w-full">
				<Button
					class="mx-auto size-min px-6 py-3 md:text-lg"
					@click="handleOpenTypeform('faq')"
				>
					{{ strings.CTA.advertiseMyProperty.label }}
					<Component
						:is="strings.CTA.advertiseMyProperty.icon"
						class="size-6"
					/>
				</Button>
			</div>
		</ContentBlock>
	</Container>
</template>
